// /src/api/backendAPI.js

import httpClient from "./httpClient";
import { devLog } from "../utils/logger";

/**
 * Run async tasks with a concurrency limit.
 * Returns results in the same order as input, with { status, value/reason }
 * shape matching Promise.allSettled.
 */
function pAllSettled(tasks, concurrency = 3) {
  const results = new Array(tasks.length);
  let next = 0;

  async function worker() {
    while (next < tasks.length) {
      const i = next++;
      try {
        results[i] = { status: "fulfilled", value: await tasks[i]() };
      } catch (err) {
        results[i] = { status: "rejected", reason: err };
      }
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, tasks.length) },
    () => worker(),
  );
  return Promise.all(workers).then(() => results);
}

/**
 * Translate the frontend units value ("km") to the backend's expected
 * wire format ("kilometers"). "miles" and any other value pass through
 * unchanged. The frontend canonicalizes on "km" internally.
 */
function toBackendUnits(units) {
  return units === "km" ? "kilometers" : units;
}

/**
 * Translate the backend units value ("kilometers") back to the frontend's
 * canonical form ("km"). "miles" and any other value pass through unchanged.
 */
function fromBackendUnits(units) {
  return units === "kilometers" ? "km" : units;
}

/**
 * Transform backend user object to app format
 * Backend: { _id, firstName, lastName, photo }
 * App: { id, name, avatar }
 */
function transformUser(user) {
  if (!user) return null;
  return {
    id: user._id || user.id,
    name: `${user.firstName} ${user.lastName}`.trim(),
    avatar: user.photo || null,
  };
}

/**
 * Transform backend note to app format
 * Handles all field renames and nested transformations
 */
function transformNote(note) {
  return {
    id: note.id,
    title: note.title || "",
    body: note.content || "",
    latitude: note.latitude,
    longitude: note.longitude,
    address: note.address || "",
    visibility: note.visibility,
    locationPrecision: note.locationPrecision || "exact",
    showAddress: note.showAddress !== undefined ? note.showAddress : true,
    images: (note.photos || []).map((img) => ({
      src: img.src,
      width: img.width,
      height: img.height,
    })),
    owner: transformUser(note.user),
    stats: note.stats || { likes: 0, comments: 0 },
    createdAt: new Date(note.createdAt).getTime(),
    updatedAt: note.updatedAt ? new Date(note.updatedAt).getTime() : null,
    editedAt: note.editedAt ? new Date(note.editedAt).getTime() : null,
    isOwner: note.isOwner || false,
    isLikedByCurrentUser: note.isLikedByCurrentUser || false,
    isViewedByCurrentUser: note.isViewedByCurrentUser || false,
    distance: note.distance,
    comments: [],
  };
}

/**
 * Transform backend comment to app format
 * Backend: { id, content, user, likes, createdAt, isLikedByCurrentUser }
 * App: { id, text, user, likes, createdAt, isLikedByCurrentUser }
 */
function transformComment(comment) {
  return {
    id: comment.id,
    user: transformUser(comment.user),
    text: comment.content,
    likes: comment.likes || 0,
    isLikedByCurrentUser: comment.isLikedByCurrentUser || false,
    createdAt: new Date(comment.createdAt).getTime(),
    editedAt: comment.editedAt ? new Date(comment.editedAt).getTime() : null,
  };
}

/**
 * Transform backend notification to app format
 */
function transformNotification(notification) {
  const actor = notification.user ? transformUser(notification.user) : null;

  // relatedNoteId may be a populated object or a plain string ID
  const noteObj =
    typeof notification.relatedNoteId === "object" &&
    notification.relatedNoteId !== null
      ? notification.relatedNoteId
      : null;
  const noteId = noteObj?._id || notification.relatedNoteId || null;

  // relatedCommentId may be a populated object or a plain string ID
  const commentObj =
    typeof notification.relatedCommentId === "object" &&
    notification.relatedCommentId !== null
      ? notification.relatedCommentId
      : null;
  const commentId = commentObj?._id || notification.relatedCommentId || null;

  // Extract coordinates from populated note's location
  const coords = noteObj?.location?.coordinates;

  return {
    id: notification._id || notification.id,
    type:
      notification.type === "system" && notification.systemType === "nearby"
        ? "nearby_note"
        : notification.type,
    actorId: actor?.id || null,
    actorName: actor?.name || null,
    avatarUrl: actor?.avatar || null,
    noteId,
    commentId,
    noteTitle: noteObj?.title || null,
    placeName: noteObj?.location?.address || null,
    coords:
      coords && coords.length === 2
        ? { lat: coords[1], lng: coords[0] }
        : null,
    commentSnippet: commentObj?.content || null,
    read: notification.read,
    createdAt: new Date(notification.createdAt).getTime(),
    message: notification.message,
  };
}

/**
 * Convert app offset to backend page number
 * App uses offset-based pagination (offset=0, offset=20, etc)
 * Backend uses page-based pagination (page=1, page=2, etc)
 *
 * Examples:
 *   offset=0, limit=20 → page=1
 *   offset=20, limit=20 → page=2
 *   offset=40, limit=20 → page=3
 */
function offsetToPage(offset, limit) {
  return Math.floor(offset / limit) + 1;
}

/**
 * All methods use JWT authentication
 */
class BackendAPI {
  // ===== NOTES API (8 methods) =====
  /**
   * Get statistics for current user's notes
   * @returns {Promise<{data: {all:number, public: number, private: number}}>}
   */
  async getMyNotesStats() {
    const response = await httpClient.get("/notes/my/stats");
    // Backend: { status: "success", data: { all: 45, public: 30, private: 15 } }
    // App: { data: { all: 45, public: 30, private: 15 } }
    return {
      data: response.data.data,
    };
  }

  /**
   * Get current user's notes with filtering and pagination
   * @param {Object} params
   * @params {string} params.filter - 'all' | 'public' | 'private'
   * @params {number} params.limit - Items per page (default: 50)
   * @params {number} params.offset - Zero-based offset for pagination
   * @returns {Promise<{data: Note[], total: number, hasMore: boolean, filter: string}>}
   */
  async getMyNotes({ filter = "all", limit = 50, offset = 0 } = {}) {
    const page = offsetToPage(offset, limit);

    const response = await httpClient.get("/notes/my", {
      params: { filter, limit, page },
    });

    // Backend: { status: "success", data: { notes: [...] }, total: 45, hasMore: true }
    const transformedNotes = response.data.data.notes.map(transformNote);

    return {
      data: transformedNotes,
      total: response.data.total,
      hasMore: response.data.hasMore,
      filter,
    };
  }

  /**
   * Get notes near a specific location
   * @params {Object} params
   * @params {number} params.latitude - Latitude coordinate
   * @params {number} params.longitude - Longitude coordinate
   * @params {number} params.radius - Search radius (default 1 mi)
   * @params {number} params.limit - Max results (default: 50)
   * @returns {Promise<{data: Note[], total: number, center: {latitude: number, longitude: number}}>}
   */
  async getNearbyNotes({ latitude, longitude, radius = 1, unit = 'mi', limit = 50 } = {}) {
    const response = await httpClient.get("/notes/nearby", {
      params: {
        latitude,
        longitude,
        radius,
        unit,
        limit,
      },
    });

    const transformedNotes = response.data.data.notes.map(transformNote);
    devLog("Transformed Notes");
    devLog(transformedNotes);

    return {
      data: transformedNotes,
      total: response.data.total,
      center: { latitude, longitude },
    };
  }

  /**
   * Get notes posted near user since their last logout (legacy bulk notifications)
   * @param {Object} options
   * @param {number} options.radius - Search radius
   * @param {string} options.unit - 'mi' or 'km'
   * @param {number} options.limit - Max notes to return
   * @returns {Promise<{data: Note[], total: number, since: string, center: Object}>}
   */
  async getNewNearbyNotes({ radius, unit = "mi", limit = 50 } = {}) {
    const response = await httpClient.get("/notes/nearby/new", {
      params: { radius, unit, limit },
    });

    return {
      data: response.data.data.notes.map(transformNote),
      total: response.data.total,
      since: response.data.since,
      center: response.data.center,
    };
  }

  /**
   * Geta single note by ID
   * @param {string} id - Note ID
   * @returns {Promise<{data: Note}>}
   */
  async getNote(id) {
    const response = await httpClient.get(`/notes/${id}`);
    return {
      data: transformNote(response.data.data),
    };
  }

  /**
   * Create a new note
   * @param {Object} noteData
   * @param {string} noteData.title - Note title
   * @param {string} noteData.body - Note content
   * @param {number} noteData.latitude - Latitude coordinate
   * @param {number} noteData.longitude - Longitude coordinate
   * @param {string} noteData.address - Location address
   * @param {string} noteData.visibility - 'public' | 'private'
   * @param {Array} noteData.images - Optional image attachments
   * @returns {Promise<{data: Note}>}
   */
  async createNote(noteData) {
    const hasFileObjects =
      noteData.images?.length > 0 && noteData.images[0] instanceof Blob;

    if (hasFileObjects) {
      // Use FormData for multipart upload
      const formData = new FormData();

      // Append note fields
      formData.append("title", noteData.title || "");
      formData.append("content", noteData.body || "");
      formData.append("location[coordinates][0]", noteData.longitude);
      formData.append("location[coordinates][1]", noteData.latitude);
      formData.append("location[address]", noteData.address || "");
      formData.append("visibility", noteData.visibility);
      formData.append("locationPrecision", noteData.locationPrecision);
      formData.append("showAddress", noteData.showAddress);

      // Append each image file
      noteData.images.forEach((file) => formData.append("photos", file));

      const response = await httpClient.post("/notes", formData);

      return {
        data: transformNote(response.data.data),
      };
    } else {
      const backendPayload = {
        title: noteData.title || "",
        content: noteData.body || "",
        location: {
          coordinates: [noteData.longitude, noteData.latitude],
          address: noteData.address || "",
        },
        //TODO - if not visibility provided, default to user's preferences
        visibility: noteData.visibility, // || setting preferences
        locationPrecision: noteData.locationPrecision,
        showAddress: noteData.showAddress,
        photos: (noteData.images || []).map((img) => ({
          url: img.src,
          width: img.width,
          height: img.height,
        })),
      };

      const response = await httpClient.post("/notes", backendPayload);

      return {
        data: transformNote(response.data.data),
      };
    }
  }

  /**
   * Update an existing note
   * @param {string} id - Note ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<{data: Note}>}
   */
  async updateNote(id, updates) {
    // Backend requires multipart/form-data for note updates
    const formData = new FormData();

    // Append text fields if provided
    if (updates.title !== undefined) {
      formData.append("title", updates.title);
    }
    if (updates.body !== undefined) {
      formData.append("content", updates.body);
    }
    if (updates.visibility !== undefined) {
      formData.append("visibility", updates.visibility);
    }
    if (updates.address !== undefined) {
      formData.append("address", updates.address);
    }
    if (updates.locationPrecision !== undefined) {
      formData.append("locationPrecision", updates.locationPrecision);
    }
    if (updates.showAddress !== undefined) {
      formData.append("showAddress", updates.showAddress);
    }

    // Handle images: separate existing photos (URLs) from new photos (Files)
    if (updates.images !== undefined) {
      const existingPhotos = [];
      const newPhotos = [];

      (updates.images || []).forEach((img) => {
        if (img instanceof Blob) {
          newPhotos.push(img);
        } else {
          // Existing photo object with src, width, height
          existingPhotos.push({
            src: img.src,
            width: img.width,
            height: img.height,
          });
        }
      });

      // Always send existingPhotos when images are being updated
      // (empty array means remove all existing photos)
      formData.append("existingPhotos", JSON.stringify(existingPhotos));

      // Append new photo files
      newPhotos.forEach((file) => formData.append("photos", file));
    }

    const response = await httpClient.patch(`/notes/${id}`, formData);
    return {
      data: transformNote(response.data.data),
    };
  }

  /**
   * Delete a note
   * @param {string} - id - Note ID
   * @returns {Promise<{success: boolean}>}
   */
  async deleteNote(id) {
    await httpClient.delete(`/notes/${id}`);
    return { success: true };
  }

  /**
   * Like/Unlike a note
   * @params {string} - id - Note ID
   * @returns {Promise<{data: Liked: boolean, count: number}>}
   */
  async toggleLike(id) {
    // Get current like state
    const noteResponse = await httpClient.get(`/notes/${id}`);
    const isCurrentlyLiked = noteResponse.data.data.isLikedByCurrentUser;

    // Call appropriate endpoint
    if (isCurrentlyLiked) {
      await httpClient.delete(`/notes/${id}/likes`);
    } else {
      await httpClient.post(`/notes/${id}/likes`);
    }

    // Fetch updated note to get new stats
    const updatedResponse = await httpClient.get(`/notes/${id}`);
    const updatedNote = updatedResponse.data.data;

    return {
      data: {
        liked: updatedNote.isLikedByCurrentUser,
        count: updatedNote.stats.likes,
      },
    };
  }

  /**
   * Get statistics for a specific note
   * @params {string} id - Note ID
   * returns {Promise<{data: {likes: number, comments: number, isLiked: boolean}}>}
   */
  async getNoteStats(id) {
    const response = await httpClient.get(`/notes/${id}/stats`);
    return {
      data: response.data.data,
    };
  }

  // ===== Comments API (5 methods) =====
  /**
   * Get paginated comments for a note
   *
   * Backend endpoint: GET /api/v1/notes/:id/comments?page={page}&limit={limit}
   *
   * @param {string} noteId - The ID of the note
   * @param {Object} options - Pagination options
   * @param {number} [options.offset=0] - Starting offset (mock pagination)
   * @param {number} [options.limit=10] - Number of comments to fetch
   * @returns {Promise<Object>} Comments response
   * @returns {Array} return.data - Array of transformed comments
   * @returns {number} return.total - Total number of comments
   * @returns {boolean} return.hasMore - Whether more comments exist
   * @returns {number} return.offset - Current offset
   * @returns {number} return.limit - Page limit
   *
   * @example
   * const response = await backendAPI.getComments('note-123', { offset: 0, limit: 10 });
   * // Returns: { data: [...], total: 25, hasMore: true, offset: 0, limit: 10 }
   */
  async getComments(noteId, { offset = 0, limit = 10 } = {}) {
    const page = offsetToPage(offset, limit);

    const response = await httpClient.get(`/notes/${noteId}/comments`, {
      params: { page, limit },
    });

    const transformedComments =
      response.data.data.comments.map(transformComment);

    return {
      data: transformedComments,
      total: response.data.total,
      hasMore: response.data.hasMore,
      offset,
      limit,
    };
  }

  /**
   * Add a new comment to a note
   *
   * Backend endpoint: POST /api/v1/notes/:id/comments
   *
   * @param {string} noteId - The ID of the note to comment on
   * @param {Object} commentData - Comment data
   * @param {string} commentData.text - The comment text
   * @returns {Promise<Object>} Comment creation response
   * @returns {Object} return.data - The created comment (transformed)
   * @returns {Object} return.stats - Updated note stats (likes, comments count)
   *
   * @example
   * const response = await backendAPI.addComment('note-123', { text: 'Great post!' });
   * // Returns: { data: { id, user, text, likes, ... }, stats: { likes: 5, comments: 3 } }
   */
  async addComment(noteId, commentData) {
    const backendPayload = {
      content: commentData.text,
    };

    const response = await httpClient.post(
      `/notes/${noteId}/comments`,
      backendPayload,
    );
    const transformedComment = transformComment(response.data.data);

    return {
      data: transformedComment,
      stats: response.data.stats,
    };
  }

  /**
   * Update an existing comment
   *
   * Backend endpoint: PATCH /api/v1/notes/:noteId/comments/:commentId
   *
   * @param {string} noteId - The ID of the note containing the comment
   * @param {string} commentId - The ID of the comment to update
   * @param {Object} updates - Fields to update
   * @param {string} [updates.text] - New comment text
   * @returns {Promise<Object>} Update response
   * @returns {Object} return.data - The updated comment (transformed)
   *
   * @example
   * const response = await backendAPI.updateComment('note-123', 'comment-456', {
   *   text: 'Updated comment text'
   * });
   */
  async updateComment(noteId, commentId, updates) {
    const backendUpdates = {};
    if (updates.text !== undefined) {
      backendUpdates.content = updates.text;
    }

    const response = await httpClient.patch(
      `/notes/${noteId}/comments/${commentId}`,
      backendUpdates,
    );

    return {
      data: transformComment(response.data.data),
    };
  }

  /**
   * Delete a comment
   *
   * Backend endpoint: DELETE /api/v1/notes/:noteId/comments/:commentId
   *
   * @param {string} noteId - The ID of the note containing the comment
   * @param {string} commentId - The ID of the comment to delete
   * @returns {Promise<Object>} Delete response
   * @returns {boolean} return.success - Always true if successful
   * @returns {Object} return.stats - Updated note stats after deletion
   *
   * @example
   * const response = await backendAPI.deleteComment('note-123', 'comment-456');
   * // Returns: { success: true, stats: { likes: 5, comments: 2 } }
   */
  async deleteComment(noteId, commentId) {
    await httpClient.delete(`/notes/${noteId}/comments/${commentId}`);

    // Fetch updated note stats
    const statsResponse = await httpClient.get(`/notes/${noteId}/stats`);

    return {
      success: true,
      stats: statsResponse.data.data,
    };
  }

  /**
   * Toggle like on a comment (like if not liked, unlike if already liked)
   *
   * Backend endpoints:
   *   - POST /api/v1/comments/:commentId/likes (to like)
   *   - DELETE /api/v1/comments/:commentId/likes (to unlike)
   *
   * Strategy: Fetch current state first to determine which endpoint to call
   *
   * @param {string} noteId - The ID of the note containing the comment
   * @param {string} commentId - The ID of the comment to toggle like on
   * @returns {Promise<Object>} Like toggle response
   * @returns {Object} return.data - Like state data
   * @returns {boolean} return.data.liked - New like state (true if now liked)
   * @returns {number} return.data.count - New like count
   *
   * @example
   * const response = await backendAPI.toggleCommentLike('note-123', 'comment-456');
   * // Returns: { data: { liked: true, count: 3 } }
   */
  async toggleCommentLike(noteId, commentId) {
    // Fetch current comment state via note comments
    const commentsResponse = await httpClient.get(`/notes/${noteId}/comments`);
    const comment = commentsResponse.data.data.comments.find(
      (c) => c.id === commentId,
    );

    if (!comment) {
      throw new Error("Comment not found");
    }

    const isCurrentlyLiked = comment.isLikedByCurrentUser;

    // Toggle based on current state
    if (isCurrentlyLiked) {
      await httpClient.delete(`/comments/${commentId}/likes`);
    } else {
      await httpClient.post(`/comments/${commentId}/likes`);
    }

    // Fetch updated comments to get new like state
    const updatedCommentsResponse = await httpClient.get(
      `/notes/${noteId}/comments`,
    );
    const updatedComment = updatedCommentsResponse.data.data.comments.find(
      (c) => c.id === commentId,
    );

    return {
      data: {
        liked: updatedComment.isLikedByCurrentUser,
        count: updatedComment.likes,
      },
    };
  }

  // ===== Notification API (6 methods) =====
  /**
   * Get paginated notifications for the current user
   *
   * Backend endpoint: GET /api/v1/notifications?filter={}&limit={}&page={}
   *
   * @param {Object} options - Query options
   * @param {string} [options.filter="all"] - Filter by type: "all"|"likes"|"comments"|"system"
   * @param {number} [options.limit=20] - Number of notifications per page
   * @param {number} [options.offset=0] - Starting offset (mock pagination)
   * @returns {Promise<Object>} Notifications response
   * @returns {Array} return.data - Array of notifications
   * @returns {number} return.total - Total number of notifications
   * @returns {boolean} return.hasMore - Whether more notifications exist
   * @returns {string} return.filter - Echo of filter parameter
   *
   * @example
   * const response = await backendAPI.getNotifications({ filter: 'likes', offset: 0, limit: 20 });
   * // Returns: { data: [...], total: 45, hasMore: true, filter: 'likes' }
   */
  async getNotifications({ filter = "all", limit = 20, offset = 0 } = {}) {
    const page = offsetToPage(offset, limit);

    const response = await httpClient.get("/notifications", {
      params: { filter, limit, page },
    });

    const transformed = (response.data.data.notifications || []).map(
      transformNotification,
    );
    const enriched = await this._enrichNotifications(transformed);

    return {
      data: enriched,
      total: response.data.total,
      hasMore: response.data.hasMore,
      filter,
    };
  }

  /**
   * Get notification statistics (unread counts by category)
   *
   * Backend endpoint: GET /api/v1/notifications/stats
   *
   * @returns {Promise<Object>} Stats response
   * @returns {Object} return.data - Statistics object
   * @returns {number} return.data.all - Total unread notifications
   * @returns {number} return.data.likes - Unread like notifications
   * @returns {number} return.data.comments - Unread comment notifications
   * @returns {number} return.data.system - Unread system notifications
   *
   * @example
   * const response = await backendAPI.getNotificationStats();
   * // Returns: { data: { all: 10, likes: 4, comments: 5, system: 1 } }
   */
  async getNotificationStats() {
    const response = await httpClient.get("/notifications/stats");
    return { data: response.data.data };
  }

  /**
   * Mark a single notification as read
   *
   * Backend endpoint: PATCH /api/v1/notifications/:id/read
   *
   * @param {string} notificationId - The ID of the notification to mark as read
   * @returns {Promise<Object>} Update response
   * @returns {Object} return.data - The updated notification
   *
   * @example
   * const response = await backendAPI.markNotificationRead('notif-123');
   * // Returns: { data: { id: 'notif-123', read: true, ... } }
   */
  async markNotificationRead(notificationId) {
    const response = await httpClient.patch(
      `/notifications/${notificationId}/read`,
    );
    return { data: response.data.data };
  }

  /**
   * Mark all notifications as read (optionally filtered by type)
   *
   * Backend endpoint: PATCH /api/v1/notifications/read-all?filter={}
   *
   * @param {string} [filter="all"] - Filter: "all"|"likes"|"comments"|"system"
   * @returns {Promise<Object>} Update response
   * @returns {Object} return.data - Result data
   * @returns {number} return.data.marked - Number of notifications marked as read
   *
   * @example
   * const response = await backendAPI.markAllNotificationsRead('likes');
   * // Returns: { data: { marked: 5 } }
   */
  async markAllNotificationsRead(filter = "all") {
    const response = await httpClient.patch(
      "/notifications/read-all",
      {},
      {
        params: { filter },
      },
    );
    return { data: response.data.data };
  }

  /**
   * Update the user's last seen timestamp for notifications
   *
   * Backend endpoint: POST /api/v1/notifications/last-seen
   *
   * Used to track when user last viewed notifications page
   * Helps determine which notifications are "new"
   *
   * @param {number} [timestamp=Date.now()] - Unix timestamp in milliseconds
   * @returns {Promise<Object>} Update response
   * @returns {Object} return.data - Result data
   * @returns {string|number} return.data.lastSeenAt - The updated timestamp
   *
   * @example
   * const response = await backendAPI.updateLastSeen(Date.now());
   * // Returns: { data: { lastSeenAt: "2024-11-29T10:30:00.000Z" } }
   */
  async updateLastSeen(timestamp = Date.now()) {
    const response = await httpClient.post("/notifications/last-seen", {
      timestamp,
    });
    return { data: response.data.data };
  }

  /**
   * Get the user's last seen timestamp for notifications
   *
   * Backend endpoint: GET /api/v1/notifications/last-seen
   *
   * @returns {Promise<Object>} Last seen response
   * @returns {Object} return.data - Result data
   * @returns {string|number} return.data.lastSeenAt - Last seen timestamp
   *
   * @example
   * const response = await backendAPI.getLastSeen();
   * // Returns: { data: { lastSeenAt: "2024-11-29T10:30:00.000Z" } }
   */
  async getLastSeen() {
    const response = await httpClient.get("/notifications/last-seen");
    return { data: response.data.data };
  }

  // ===== Settings API (4 methods) =====
  /**
   * Get user settings for the current user
   *
   * Backend endpoint: GET /api/v1/settings
   *
   * @returns {Promise<Object>} Settings response
   * @returns {Object} return.data - User settings object
   * @returns {string} return.data.defaultVisibility - "public" or "private"
   * @returns {string} return.data.units - "miles" or "km" (backend's "kilometers" is mapped to "km" here)
   * @returns {string} return.data.theme - "auto"|"light"|"dark"
   * @returns {boolean} return.data.showMyLocationOnMap - Show user location on map
   * @returns {Object} return.data.notifications - Notification preferences
   * @returns {boolean} return.data.notifications.likes - Receive like notifications
   * @returns {boolean} return.data.notifications.comments - Receive comment notifications
   * @returns {boolean} return.data.notifications.system - Receive system notifications
   *
   * @example
   * const response = await backendAPI.getUserSettings();
   * // Returns: { data: { defaultVisibility: 'public', units: 'miles', ... } }
   */
  async getUserSettings() {
    const response = await httpClient.get("/settings");
    // Backend returns { data: { settings: {...} } }, unwrap the settings object
    const settings = response.data.data.settings;
    return {
      data: { ...settings, units: fromBackendUnits(settings.units) },
    };
  }

  /**
   * Update user settings (partial update supported)
   *
   * Backend endpoint: PATCH /api/v1/settings
   *
   * Backend handles deep merging for nested notifications object
   *
   * @param {Object} updates - Settings fields to update
   * @param {string} [updates.defaultVisibility] - "public" or "private"
   * @param {string} [updates.units] - "miles" or "km" (mapped to backend's "kilometers" on the wire)
   * @param {string} [updates.theme] - "auto"|"light"|"dark"
   * @param {boolean} [updates.showMyLocationOnMap] - Show user location
   * @param {Object} [updates.notifications] - Notification preferences (partial)
   * @param {boolean} [updates.notifications.likes] - Like notifications
   * @param {boolean} [updates.notifications.comments] - Comment notifications
   * @param {boolean} [updates.notifications.system] - System notifications
   * @returns {Promise<Object>} Update response
   * @returns {Object} return.data - Updated settings object (units mapped back to "km")
   *
   * @example
   * const response = await backendAPI.updateUserSettings({
   *   defaultVisibility: 'private',
   *   notifications: { likes: false }  // Only updates likes, keeps comments/system
   * });
   */
  async updateUserSettings(updates) {
    // TODO - should updates be cleaned up?
    const payload =
      updates && "units" in updates
        ? { ...updates, units: toBackendUnits(updates.units) }
        : updates;
    const response = await httpClient.patch("/settings", payload);
    // Backend returns { data: { settings: {...} } }, unwrap the settings object
    const settings = response.data.data.settings;
    return {
      data: { ...settings, units: fromBackendUnits(settings.units) },
    };
  }

  /**
   * Change user password (requires current password for security)
   *
   * Backend endpoint: PATCH /api/v1/auth/update-password
   *
   * @param {Object} passwordData - Password change data
   * @param {string} passwordData.currentPassword - Current password for verification
   * @param {string} passwordData.newPassword - New password (min 8 characters)
   * @returns {Promise<Object>} Change response
   * @returns {Object} return.data - Result data
   * @returns {boolean} return.data.success - Always true if successful
   *
   * @example
   * const response = await backendAPI.changePassword({
   *   currentPassword: 'oldPassword123',
   *   newPassword: 'newSecurePassword456'
   * });
   * // Returns: { data: { success: true } }
   */
  async changePassword({ currentPassword, newPassword, confirmPassword }) {
    const response = await httpClient.patch("/auth/update-password", {
      currentPassword: currentPassword,
      newPassword: newPassword,
      newPasswordConfirm: confirmPassword,
    });
    return { data: response.data.data };
  }

  /**
   * Delete user account (soft delete with 30-day grace period)
   *
   * Backend endpoint: DELETE /api/v1/users/
   *
   * Requires password confirmation for security
   * Account is soft-deleted (can be recovered within 30 days)
   *
   * @param {Object} deleteData - Account deletion data
   * @param {string} deleteData.password - User password for confirmation
   * @param {string} [deleteData.reason] - Optional reason for deletion
   * @returns {Promise<Object>} Delete response
   * @returns {Object} return.data - Result data
   * @returns {boolean} return.data.success - Always true if successful
   * @returns {string} return.data.deletedAt - Deletion timestamp
   *
   * @example
   * const response = await backendAPI.deleteAccount({
   *   password: 'myPassword123',
   *   reason: 'No longer need the service'
   * });
   * // Returns: { data: { success: true, deletedAt: "2024-11-29T10:30:00.000Z" } }
   */
  async deleteAccount({ password, reason }) {
    const response = await httpClient.delete("/users/", {
      data: { password, reason },
    });
    return { data: response.data.data };
  }

  // ===== Reporting API (2 methods) =====
  /**
   * Report a note for inappropriate content
   *
   * Backend endpoint: POST /api/v1/notes/:id/report
   *
   * @param {string} noteId - The ID of the note to report
   * @param {string} reason - Report reason: "spam"|"harassment"|"inappropriate"|"other"
   * @param {string} [details=""] - Additional details about the report
   * @returns {Promise<Object>} Report response
   * @returns {Object} return.data - Result data
   * @returns {boolean} return.data.success - Always true if successful
   * @returns {string} return.data.reportId - ID of the created report
   * @returns {string} return.data.message - Success message
   *
   * @example
   * const response = await backendAPI.reportNote('note-123', 'spam', 'This is spam content');
   * // Returns: { data: { success: true, reportId: 'report-456', message: '...' } }
   */
  async reportNote(noteId, reason, details = "") {
    const response = await httpClient.post(`/notes/${noteId}/report`, {
      reason,
      details,
    });
    return { data: response.data.data };
  }

  /**
   * Report a comment for inappropriate content
   *
   * Backend endpoint: POST /api/v1/notes/:noteId/comments/:commentId/report
   *
   * @param {string} noteId - The ID of the note containing the comment
   * @param {string} commentId - The ID of the comment to report
   * @param {string} reason - Report reason: "spam"|"harassment"|"inappropriate"|"other"
   * @param {string} [details=""] - Additional details about the report
   * @returns {Promise<Object>} Report response
   * @returns {Object} return.data - Result data
   * @returns {boolean} return.data.success - Always true if successful
   * @returns {string} return.data.reportId - ID of the created report
   * @returns {string} return.data.message - Success message
   *
   * @example
   * const response = await backendAPI.reportComment('note-123', 'comment-456', 'harassment', 'Inappropriate language');
   * // Returns: { data: { success: true, reportId: 'report-789', message: '...' } }
   */
  async reportComment(noteId, commentId, reason, details = "") {
    const response = await httpClient.post(
      `/notes/${noteId}/comments/${commentId}/report`,
      {
        reason,
        details,
      },
    );
    return { data: response.data.data };
  }

  // ===== Feedback API API (3 methods) =====
  /**
   * Submit an improvement suggestion
   *
   * Backend endpoint: POST /api/v1/feedback/improvement
   *
   * @param {Object} feedbackData - Improvement feedback data
   * @param {string} feedbackData.category - Category of improvement
   * @param {string} feedbackData.description - Detailed description
   * @returns {Promise<Object>} Feedback response
   * @returns {Object} return.data - The created feedback object
   * @returns {string} return.data.id - Feedback ID
   * @returns {string} return.data.type - Always "improvement"
   * @returns {string} return.data.status - Status (e.g., "new")
   *
   * @example
   * const response = await backendAPI.submitImprovement({
   *   category: 'UI/UX',
   *   description: 'Add dark mode support'
   * });
   */
  async submitImprovement({ category, description }) {
    const response = await httpClient.post("/feedback/improvement", {
      category,
      description,
    });
    return { data: response.data.data };
  }

  /**
   * Submit a bug report
   *
   * Backend endpoint: POST /api/v1/feedback/bug
   *
   * @param {Object} feedbackData - Bug report data
   * @param {string} feedbackData.category - Category of bug
   * @param {string} feedbackData.description - Detailed bug description
   * @returns {Promise<Object>} Feedback response
   * @returns {Object} return.data - The created feedback object
   * @returns {string} return.data.id - Feedback ID
   * @returns {string} return.data.type - Always "bug"
   * @returns {string} return.data.status - Status (e.g., "new")
   *
   * @example
   * const response = await backendAPI.submitBugReport({
   *   category: 'Maps',
   *   description: 'Map not loading on iOS Safari'
   * });
   */
  async submitBugReport({ category, description }) {
    const response = await httpClient.post("/feedback/bug", {
      category,
      description,
    });

    return { data: response.data.data };
  }

  /**
   * Submit a support request
   *
   * Backend endpoint: POST /api/v1/feedback/support
   *
   * @param {Object} feedbackData - Support request data
   * @param {string} feedbackData.category - Category of support needed
   * @param {string} feedbackData.description - Detailed support request
   * @returns {Promise<Object>} Feedback response
   * @returns {Object} return.data - The created feedback object
   * @returns {string} return.data.id - Feedback ID
   * @returns {string} return.data.type - Always "support"
   * @returns {string} return.data.status - Status (e.g., "new")
   *
   * @example
   * const response = await backendAPI.submitSupportRequest({
   *   category: 'Account',
   *   description: 'Cannot reset password'
   * });
   */
  async submitSupportRequest({ category, description }) {
    const response = await httpClient.post("/feedback/support", {
      category,
      description,
    });
    return { data: response.data.data };
  }

  /**
   * Enrich Transformed notifications with note and comment details
   * Batch-fetches unique notes and comments in parallel, then merges
   * noteTitle, placeName, coords, and comment Snippet onto each notification
   */
  async _enrichNotifications(notifications) {
    if (!notifications.length) return notifications;

    // Skip enrichment for notifications already populated by the backend
    const needsNoteEnrichment = notifications.filter(
      (n) => n.noteId && !n.noteTitle,
    );
    const needsCommentEnrichment = notifications.filter(
      (n) => n.commentId && n.noteId && !n.commentSnippet,
    );

    // --- 1. Batch-fetch unique notes (concurrency-limited) ---
    const noteIds = [
      ...new Set(needsNoteEnrichment.map((n) => n.noteId)),
    ];

    const noteMap = new Map();
    if (noteIds.length) {
      const noteResults = await pAllSettled(
        noteIds.map((id) => () => this.getNote(id)),
        3,
      );
      noteIds.forEach((id, i) => {
        if (noteResults[i].status === "fulfilled") {
          noteMap.set(id, noteResults[i].value.data);
        }
      });
    }

    // --- 2. Batch-fetch comments for snippet lookup (concurrency-limited) ---
    const commentNoteIds = [
      ...new Set(needsCommentEnrichment.map((n) => n.noteId)),
    ];

    const commentsMap = new Map();
    if (commentNoteIds.length) {
      const commentResults = await pAllSettled(
        commentNoteIds.map((noteId) => () => this.getComments(noteId, { limit: 50 })),
        3,
      );
      commentNoteIds.forEach((noteId, i) => {
        if (commentResults[i].status === "fulfilled") {
          commentsMap.set(noteId, commentResults[i].value.data);
        }
      });
    }

    // --- 3. Merge onto notifications (only fill in missing fields) ---
    return notifications.map((n) => {
      const note = noteMap.get(n.noteId);
      const enriched = { ...n };

      if (note) {
        enriched.noteTitle = enriched.noteTitle || note.title;
        enriched.placeName = enriched.placeName || note.address || null;
        enriched.coords =
          enriched.coords ||
          (note.latitude != null && note.longitude != null
            ? { lat: note.latitude, lng: note.longitude }
            : null);
      }

      if (n.commentId && !enriched.commentSnippet) {
        const comment = (commentsMap.get(n.noteId) || []).find(
          (c) => c.id === n.commentId,
        );
        if (comment) {
          enriched.commentSnippet = comment.text;
        }
      }

      return enriched;
    });
  }
}

export const backendAPI = new BackendAPI();
